import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { Send, Plus, Loader2, Sparkles, MessageSquareText, Coins } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { ChatMessage } from "@/components/ChatMessage";
import { SkillPreviewPane } from "@/components/SkillPreviewPane";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api, API, apiErr } from "@/lib/api";
import { purchaseOrDownload } from "@/lib/payments";

const SUGGESTIONS = [
  "A skill that extracts line items & totals from PDF invoices",
  "A skill that writes conventional-commit messages from a git diff",
  "A skill that turns rough notes into a polished blog post",
  "A skill that converts CSV data into clean markdown tables",
];

// ponytail: user-facing effort labels; internal tier keys (sonnet/opus) stay the wire values.
const TIER_LABEL = { sonnet: "Normal", opus: "Pro" };

// Canned scoping interview (zero LLM cost). The initial idea answers "what it does";
// these are asked one at a time as assistant bubbles before a single generation call.
const INTERVIEW = [
  { key: "trigger", q: "Nice. When should an agent reach for this? List the words or situations that should switch it on - like 'messy meeting notes', 'a PDF invoice', or 'summarize this thread'. Being specific here is what makes it trigger reliably." },
  { key: "inputs", q: "What will you give it to work with? A file, some text, a link - whatever you'd hand it. (If nothing, just say 'nothing.')" },
  { key: "steps", q: "If you did this yourself by hand, what steps would you take? A rough list is perfect - I'll turn it into reliable instructions. Not sure? Say 'not sure' and I'll figure it out." },
  { key: "outputs", q: "What should you get back when it's done? Describe what a great result looks like." },
  { key: "tools", q: "Does it need to reach outside itself - run code, use an app, call an API, that kind of thing? If you're not sure, just say 'not sure' and I'll decide for you." },
  { key: "example", q: "Last one, and it's optional: paste a quick example or a tricky case it should handle. Not sure? Type 'skip' and I'll build it now." },
];

let _msgSeq = 0;
const nextMsgId = () => `msg-${Date.now().toString(36)}-${(_msgSeq += 1)}`;

function buildBrief(a, kind = "skill") {
  const isAgent = kind === "agent";
  const noun = isAgent ? "Claude Code subagent" : "Claude Skill";
  const lines = [
    `Create a complete, production-quality ${noun} now. Do not ask any more questions — generate the full ${isAgent ? "agent" : "skill"}.`,
    `What it does: ${a.purpose || "(unspecified)"}`,
    `When to use it (the trigger): ${a.trigger || "(unspecified)"}`,
    `Inputs: ${a.inputs || "(unspecified)"}`,
    `How to do it (rough steps from the user): ${a.steps || "(use your best judgment)"}`,
    `Outputs: ${a.outputs || "(unspecified)"}`,
    `Scripts / tools / APIs: ${a.tools || "none"}`,
  ];
  const ex = (a.example || "").trim().toLowerCase();
  if (a.example && !["skip", "none", "no", "n/a"].includes(ex)) {
    lines.push(`Example / edge cases: ${a.example}`);
  }
  lines.push(
    isAgent
      ? "Apply best practices: a precise description of when to delegate to this agent, the fewest tools it actually needs, and clear second-person instructions in the system prompt."
      : "Apply best practices: write the `description` in the third person, packing in BOTH what it does AND the concrete trigger words/situations above (lean slightly pushy — agents under-trigger). Then clear step-by-step instructions and modular helper files referenced by relative path."
  );
  return lines.join("\n");
}

function replaceLastAssistant(list, content, streaming) {
  const next = [...list];
  for (let i = next.length - 1; i >= 0; i--) {
    if (next[i].role === "assistant") {
      next[i] = { ...next[i], content, streaming };
      break;
    }
  }
  return next;
}

export default function SkillBuilder() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Phase 6: a session builds a skill (default) or an agent. Chosen at the new-session entry
  // point via ?kind=agent; the server stores it and returns it on load.
  const [kind, setKind] = useState("skill");
  const [messages, setMessages] = useState([]);
  const [skill, setSkill] = useState(null);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [booting, setBooting] = useState(true);
  const [mobileView, setMobileView] = useState("chat");
  // interviewIndex: -1 = awaiting initial idea | 0..N-1 = awaiting INTERVIEW[i] | N = interview done
  const [interviewIndex, setInterviewIndex] = useState(-1);
  const [answers, setAnswers] = useState({});
  // Phase 4b — credit-metered generation: a charge buys a 5-turn window; every send burns a
  // turn; at 0 the next send opens a new window (charges a credit). `outOfCredits` is set when
  // the server refuses for lack of balance.
  const [turnsLeft, setTurnsLeft] = useState(null);
  const [tier, setTier] = useState(null);
  const [selectedTier, setSelectedTier] = useState("sonnet");
  const [balance, setBalance] = useState(null);
  const [outOfCredits, setOutOfCredits] = useState(false);
  const scrollRef = useRef(null);
  // Phase 9a: ?org=<slug> targets a new build at an org's private catalog instead of the
  // public gallery — a session is bound to its org for its whole life.
  const orgSlug = searchParams.get("org") || "";

  const creditsLabel = (cents) =>
    cents == null ? "—" : `${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })} cr`;

  // Ensure a session exists / load existing
  useEffect(() => {
    let active = true;
    (async () => {
      if (!sessionId) {
        try {
          const reqKind = searchParams.get("kind") === "agent" ? "agent" : "skill";
          const { data } = await api.post("/chat/sessions", { kind: reqKind, org_slug: orgSlug || undefined });
          if (active) navigate(`/build/${data.id}${orgSlug ? `?org=${orgSlug}` : ""}`, { replace: true });
        } catch (e) {
          toast.error("Could not start a session");
        }
        return;
      }
      try {
        const { data } = await api.get(`/chat/sessions/${sessionId}`);
        if (!active) return;
        setKind(data.kind === "agent" ? "agent" : "skill");
        setMessages((data.messages || []).map((m) => ({ ...m, id: m.id || nextMsgId() })));
        setSkill(data.skill || null);
        if (typeof data.turns_left === "number") setTurnsLeft(data.turns_left);
        if (data.tier) { setTier(data.tier); setSelectedTier(data.tier); }
        if (data.balance) setBalance(data.balance);
        if (data.skill || (data.messages || []).length > 0) {
          setInterviewIndex(INTERVIEW.length); // already built / mid-conversation
        } else {
          setInterviewIndex(-1); // fresh — run the interview
        }
      } catch (e) {
        toast.error("Session not found");
        navigate("/build", { replace: true });
      } finally {
        if (active) setBooting(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [sessionId, navigate, searchParams, orgSlug]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, generating, interviewIndex]);

  // Single LLM generation call (used for both the first build and refinements)
  const runGeneration = useCallback(
    async (promptText) => {
      setStreaming(true);
      setGenerating(false);
      setMessages((prev) => [...prev, { id: nextMsgId(), role: "assistant", content: "", streaming: true }]);
      let acc = "";
      try {
        const res = await fetch(`${API}/chat/sessions/${sessionId}/message`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: promptText, tier: selectedTier }),
        });
        if (!res.ok || !res.body) throw new Error("stream failed");
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buffer.indexOf("\n\n")) >= 0) {
            const raw = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const line = raw.trim();
            if (!line.startsWith("data:")) continue;
            let json;
            try {
              json = JSON.parse(line.slice(5).trim());
            } catch (e) {
              continue;
            }
            if (json.type === "delta") {
              acc += json.content;
              setMessages((prev) => replaceLastAssistant(prev, acc, true));
            } else if (json.type === "status" && json.value === "generating") {
              setGenerating(true);
            } else if (json.type === "done") {
              if (json.message) acc = json.message;
              setMessages((prev) => replaceLastAssistant(prev, acc || "Here's your skill — preview it on the right.", false));
              if (json.skill) {
                setSkill(json.skill);
                setMobileView("preview");
              }
              if (typeof json.turns_left === "number") setTurnsLeft(json.turns_left);
              if (json.tier) setTier(json.tier);
              if (json.balance) setBalance(json.balance);
              setOutOfCredits(false);
              setGenerating(false);
            } else if (json.type === "error") {
              const m = json.message || "Something went wrong. Please try again.";
              if (json.code === "insufficient_credits") {
                setOutOfCredits(true);
                if (json.balance) setBalance(json.balance);
                // Drop the optimistic empty assistant bubble — no model call happened.
                setMessages((prev) => prev.filter((mm) => !(mm.streaming && !mm.content)));
              } else {
                setMessages((prev) => replaceLastAssistant(prev, acc || m, false));
              }
              toast.error(m);
            }
          }
        }
      } catch (e) {
        toast.error("Connection error");
        setMessages((prev) => replaceLastAssistant(prev, acc || "Connection error. Please try again.", false));
      } finally {
        setStreaming(false);
        setGenerating(false);
      }
    },
    // selectedTier was missing here — the picker silently didn't apply to the request body
    // until the session changed (stale closure). Real bug, found by the lint gate.
    [sessionId, selectedTier]
  );

  const startInterview = useCallback((idea) => {
    setAnswers({ purpose: idea });
    setMessages((prev) => [
      ...prev,
      { id: nextMsgId(), role: "user", content: idea },
      { id: nextMsgId(), role: "assistant", content: INTERVIEW[0].q },
    ]);
    setInterviewIndex(0);
  }, []);

  const answerInterview = useCallback(
    (text) => {
      const step = INTERVIEW[interviewIndex];
      const nextAnswers = { ...answers, [step.key]: text };
      setAnswers(nextAnswers);
      const isLast = interviewIndex >= INTERVIEW.length - 1;
      setMessages((prev) => {
        const base = [...prev, { id: nextMsgId(), role: "user", content: text }];
        if (!isLast) base.push({ id: nextMsgId(), role: "assistant", content: INTERVIEW[interviewIndex + 1].q });
        return base;
      });
      if (isLast) {
        setInterviewIndex(INTERVIEW.length);
        runGeneration(buildBrief(nextAnswers, kind));
      } else {
        setInterviewIndex(interviewIndex + 1);
      }
    },
    [answers, interviewIndex, runGeneration, kind]
  );

  const send = useCallback(
    (text) => {
      const msg = (text ?? input).trim();
      if (!msg || streaming || generating || !sessionId) return;
      setInput("");
      if (skill) {
        setMessages((prev) => [...prev, { id: nextMsgId(), role: "user", content: msg }]);
        runGeneration(msg);
      } else if (interviewIndex === -1) {
        startInterview(msg);
      } else if (interviewIndex < INTERVIEW.length) {
        answerInterview(msg);
      } else {
        // interview finished but generation didn't yield a skill — retry as a build instruction
        setMessages((prev) => [...prev, { id: nextMsgId(), role: "user", content: msg }]);
        runGeneration(msg);
      }
    },
    [input, streaming, generating, sessionId, skill, interviewIndex, startInterview, answerInterview, runGeneration]
  );

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Also the kind switch: the old "New Agent" nav button was just this page with ?kind=agent,
  // so the toggle lives here now (2026-07-14). Called as an onClick handler it gets an event,
  // which fails the string check and keeps the current kind.
  const newChat = async (nextKind) => {
    const k = nextKind === "agent" || nextKind === "skill" ? nextKind : kind;
    try {
      const { data } = await api.post("/chat/sessions", { kind: k, org_slug: orgSlug || undefined });
      setKind(k);
      setMessages([]);
      setSkill(null);
      setAnswers({});
      setInterviewIndex(-1);
      setTurnsLeft(null);
      setTier(null);
      setOutOfCredits(false);
      navigate(`/build/${data.id}${orgSlug ? `?org=${orgSlug}` : ""}`);
    } catch (e) {
      toast.error("Could not start a new session");
    }
  };

  const handleDownload = async () => {
    if (!skill) return;
    try {
      const res = await purchaseOrDownload(skill.id, skill.slug);
      toast.success(res.action === "downloaded" ? "Skill downloaded" : "Purchased & downloaded");
      if (res.balance) setBalance(res.balance);
    } catch (e) {
      if (e.code === "insufficient_credits") {
        toast.error("Not enough credits — top up your wallet.");
        navigate("/wallet");
      } else {
        toast.error(apiErr(e, e.message || "Download failed"));
      }
    }
  };

  const interviewing = !skill && interviewIndex >= 0 && interviewIndex < INTERVIEW.length;
  const showEmpty = messages.length === 0 && !streaming && !skill && interviewIndex === -1;

  let placeholder = "Describe the skill you want to build…";
  if (skill) placeholder = "Refine your skill — e.g. \u201cadd error handling\u201d…";
  else if (interviewing) placeholder = "Type your answer…";

  return (
    <AppShell fullWidth>
      <div className="mx-auto h-[calc(100vh-3.5rem)] max-w-[1400px] px-3 py-3 sm:px-5 md:h-[calc(100vh-4rem)] md:py-5">
        {/* Mobile view toggle */}
        <div className="mb-3 flex items-center gap-2 lg:hidden">
          <Button variant={mobileView === "chat" ? "default" : "outline"} size="sm" onClick={() => setMobileView("chat")}
            className={mobileView === "chat" ? "bg-[color:var(--rb-accent)] text-[color:var(--rb-void)]" : "border-[color:var(--rb-border)]"}>Chat</Button>
          <Button variant={mobileView === "preview" ? "default" : "outline"} size="sm" onClick={() => setMobileView("preview")}
            className={mobileView === "preview" ? "bg-[color:var(--rb-accent)] text-[color:var(--rb-void)]" : "border-[color:var(--rb-border)]"}>Preview</Button>
        </div>

        <div className="grid h-full min-h-0 grid-cols-1 gap-4 lg:grid-cols-[0.95fr_1.05fr]">
          {/* Chat panel */}
          <div className={`flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] ${mobileView === "preview" ? "hidden lg:flex" : "flex"}`}>
            <div className="flex items-center justify-between border-b border-[color:var(--rb-border)] px-4 py-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[color:var(--rb-accent)]" />
                <span className="rb-display text-sm font-semibold tracking-wide text-[color:var(--rb-text)]">{kind === "agent" ? "Agent Builder" : "Skill Builder"}</span>
                {orgSlug && (
                  <Badge variant="outline" data-testid="builder-org-badge" className="gap-1 border-[color:rgba(212,175,55,0.4)] text-[color:var(--rb-gold)]">
                    For {orgSlug}
                  </Badge>
                )}
                {interviewing && (
                  <Badge variant="outline" className="gap-1 border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]" data-testid="interview-progress">
                    <MessageSquareText className="h-3 w-3" /> Scoping · {interviewIndex + 2}/{INTERVIEW.length + 1}
                  </Badge>
                )}
                {streaming && (
                  <Badge variant="outline" className="gap-1 border-[color:rgba(45,212,191,0.3)] text-[color:var(--rb-accent)]">
                    <Loader2 className="h-3 w-3 animate-spin" /> {generating ? "Forging…" : "Thinking…"}
                  </Badge>
                )}
                {turnsLeft !== null && !streaming && (
                  <Badge variant="outline"
                    data-testid="gen-turns-left"
                    className={`gap-1 ${turnsLeft > 0 ? "border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]" : "border-[color:rgba(212,175,55,0.4)] text-[color:var(--rb-gold)]"}`}>
                    <Coins className="h-3 w-3" />
                    {turnsLeft > 0 ? `${turnsLeft} turn${turnsLeft === 1 ? "" : "s"} left${tier ? ` · ${TIER_LABEL[tier] || tier}` : ""}` : "Next build spends 1 credit"}
                  </Badge>
                )}
                {balance && !streaming && (
                  <Badge variant="outline" data-testid="gen-balance"
                    className="gap-1 border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]">
                    {creditsLabel(balance.spendable_cents)}
                  </Badge>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={newChat} className="text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text)]" data-testid="new-chat-button">
                <Plus className="mr-1 h-3.5 w-3.5" /> New
              </Button>
            </div>

            <div ref={scrollRef} data-testid="skill-builder-chat-scroll" className="min-h-0 flex-1 space-y-4 overflow-auto px-4 py-5">
              {booting ? (
                <div className="flex h-full items-center justify-center text-sm text-[color:var(--rb-text-faint)]"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
              ) : showEmpty ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <Sparkles className="h-8 w-8 text-[color:var(--rb-accent)]" />
                  <h2 className="rb-display mt-4 text-xl font-semibold text-[color:var(--rb-text)]">What {kind === "agent" ? "agent" : "skill"} do you want to build?</h2>
                  <p className="mt-2 max-w-sm text-sm text-[color:var(--rb-text-faint)]">Describe it in a sentence. I'll ask a few quick questions to scope it right, then build a reliable, best-practice skill.</p>
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-[color:var(--rb-text-faint)]" data-testid="build-cost-note">
                    <Coins className="h-3.5 w-3.5 text-[color:var(--rb-gold)]" /> Building spends 1 credit and gives you a 5-turn window to refine — the download is free.
                  </p>
                  <button onClick={() => newChat(kind === "agent" ? "skill" : "agent")} data-testid="builder-kind-switch"
                    className="mt-2 text-xs text-[color:var(--rb-text-faint)] underline underline-offset-2 hover:text-[color:var(--rb-text)]">
                    {kind === "agent" ? "Want a skill instead? Switch to the skill builder" : "Want a Claude Code subagent instead? Switch to the agent builder"}
                  </button>
                  <div className="mt-6 grid w-full max-w-md gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => send(s)} data-testid="suggestion-chip"
                        className="rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] px-4 py-2.5 text-left text-sm text-[color:var(--rb-text-dim)] transition-colors hover:border-[color:var(--rb-border-strong)] hover:text-[color:var(--rb-text)]">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((m) => (
                  <ChatMessage key={m.id} role={m.role} content={m.content || (m.streaming && !generating ? "" : m.content)} streaming={m.streaming && !generating && !m.content} />
                ))
              )}
              {generating && (
                <div className="flex items-center gap-2 text-xs text-[color:var(--rb-accent)]">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Forging SKILL.md & files with best practices…
                </div>
              )}
            </div>

            <div className="border-t border-[color:var(--rb-border)] p-3">
              {outOfCredits && (
                <p className="mb-2 px-1 text-xs text-[color:var(--rb-gold)]" data-testid="insufficient-credits-note">
                  You're out of credits. <Link to="/wallet" data-testid="gen-topup-link" className="underline hover:text-[color:var(--rb-text)]">Top up your wallet</Link> to keep building.
                </p>
              )}
              {/* Out of turns while refining an existing skill — spell out the cost of a new window. */}
              {skill && turnsLeft === 0 && !streaming && !outOfCredits && (
                <p className="mb-2 px-1 text-xs text-[color:var(--rb-gold)]" data-testid="refine-cost-note">
                  Out of refinement turns — 1 credit buys 5 more turns, or 2 for Pro help.
                </p>
              )}
              {/* Tier picker — only at a window boundary (turnsLeft 0/null); locked mid-window (B3). */}
              {(turnsLeft === null || turnsLeft <= 0) && !streaming && (
                <div className="mb-2 flex items-center gap-2 px-1 text-xs text-[color:var(--rb-text-faint)]" data-testid="gen-tier-picker">
                  <span>Effort:</span>
                  {["sonnet", "opus"].map((t) => (
                    <button key={t} onClick={() => setSelectedTier(t)} data-testid={`gen-tier-${t}`}
                      className={`rounded-md border px-2 py-0.5 transition-colors ${selectedTier === t ? "border-[color:var(--rb-accent)] text-[color:var(--rb-accent)]" : "border-[color:var(--rb-border)] hover:text-[color:var(--rb-text)]"}`}>
                      {t === "sonnet" ? "Normal · 1 cr" : "Pro · 2 cr"}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-code)] p-2 focus-within:border-[color:var(--rb-border-strong)]">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={placeholder}
                  data-testid="skill-builder-chat-composer"
                  className="min-h-[44px] max-h-40 resize-none border-0 bg-transparent text-sm text-[color:var(--rb-text)] focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-60"
                />
                <Button onClick={() => send()} disabled={streaming || generating || !input.trim()} data-testid="skill-builder-chat-send-button"
                  className="h-10 w-10 shrink-0 rounded-lg bg-[color:var(--rb-accent)] p-0 text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)] disabled:opacity-40">
                  {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Preview panel */}
          <div className={`h-full min-h-0 min-w-0 ${mobileView === "chat" ? "hidden lg:block" : "block"}`}>
            {/* The builder always shows the current user's OWN skill — never lock it. Generation is
                already metered per window, so the build is the only charge; download is free here. */}
            <SkillPreviewPane
              skill={skill}
              generating={generating}
              locked={false}
              onDownload={skill ? handleDownload : null}
              downloadLabel="Download .zip"
            />
          </div>
        </div>
      </div>
    </AppShell>
  );
}
